import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { faChevronDown, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForceRender } from 'customHooks/useForceRender';
import { GOOGLE_MAPS_CALLBACK_NAME, useGoogleMaps } from 'customHooks/useGoogleMaps';
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete';

interface GooglePlacesComboboxOnSelectArgs {
    suggestion: google.maps.places.AutocompletePrediction;
    result: google.maps.places.PlaceResult;
}
interface GooglePlacesComboboxProps extends Combobox.ComboboxProps {
    defaultSearchValue?: string;
    onSelect?: ({ suggestion, result }: GooglePlacesComboboxOnSelectArgs) => void;
    onClear?: () => void;
}

const DEBOUNCE_INTERVAL = 300;
const MIN_VALUE_FOR_SEARCH = 3;

const GooglePlacesCombobox = forwardRef(
    (
        {
            children,
            onSelect,
            onClear,
            value,
            defaultSearchValue = '',
            ...rest
        }: GooglePlacesComboboxProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        const [searchValue, setSearchValue] = useState(defaultSearchValue);
        const { key, forceRender } = useForceRender();

        useGoogleMaps();

        const {
            ready,
            suggestions: { data: googleMapSuggestions },
            setValue: setGoogleMapsValue,
            clearSuggestions,
        } = usePlacesAutocomplete({
            callbackName: GOOGLE_MAPS_CALLBACK_NAME,
            debounce: DEBOUNCE_INTERVAL,
        });

        const chooseSuggestion = async (suggestion: google.maps.places.AutocompletePrediction) => {
            const result = await getDetails({ placeId: suggestion.place_id });
            onSelect?.({ suggestion, result: result as google.maps.places.PlaceResult });
            clearSuggestions();
            setSearchValue('');
        };

        useEffect(() => {
            if (ready && searchValue.length >= MIN_VALUE_FOR_SEARCH) {
                setGoogleMapsValue(searchValue);
            }
        }, [searchValue, ready, setGoogleMapsValue]);

        const foundItem = googleMapSuggestions.find(item => item.place_id === value?.at(0));
        useEffect(() => {
            if (foundItem) forceRender();
        }, [foundItem, forceRender]);

        return (
            <Combobox.Root value={value} allowMultiple={false} {...rest}>
                <Combobox.Trigger aria-label="Select address" ref={forwardedRef}>
                    <Combobox.Select>
                        <Combobox.SelectValue placeholder="Start typing your address" key={key} />
                        <div className="flex items-center gap-2">
                            {value && value.length > 0 && (
                                <Button
                                    variant="none"
                                    className="px-1.5 py-1"
                                    onClick={() => onClear?.()}
                                    aria-label="clear selection"
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="size-3.5 text-gray-500"
                                    />
                                </Button>
                            )}
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="size-3.5 text-gray-400"
                            />
                        </div>
                    </Combobox.Select>
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search value={searchValue} onChangeValue={setSearchValue} />
                    <Combobox.Items>
                        {googleMapSuggestions.map(suggestion => (
                            <Combobox.Item
                                value={suggestion.place_id}
                                key={suggestion.place_id}
                                onSelectItem={({ selectedItem }) => {
                                    const found = googleMapSuggestions.find(
                                        item => item.place_id === selectedItem,
                                    );
                                    if (found) {
                                        chooseSuggestion(found);
                                    }
                                }}
                            >
                                <Combobox.ItemText>
                                    <p className="font-medium">{suggestion.description}</p>
                                </Combobox.ItemText>
                            </Combobox.Item>
                        ))}
                        {googleMapSuggestions.length === 0 && (
                            <Combobox.NoneFoundText className="flex flex-col items-center justify-center gap-1 p-4">
                                <p className="font-medium">No results found</p>
                                <p className="text-sm">
                                    Try a different search, or enter your address manually
                                </p>
                            </Combobox.NoneFoundText>
                        )}
                    </Combobox.Items>
                </Combobox.Popover>
            </Combobox.Root>
        );
    },
);

export { GooglePlacesCombobox };
export type { GooglePlacesComboboxProps, GooglePlacesComboboxOnSelectArgs };
